import type { LabeledValuePayload } from '@Application/Users/UserStatuses/Actions/userStatusesFetched';
import type { DocumentPayload } from '@Application/Beneficiaries/Documents/Actions/documentsFetched';

/**
 * Already typed by the server according to `fieldType.value`: `text` → string, `number` → number,
 * `boolean` → boolean, `date` → an `AAAA-MM-JJ` calendar string — never converted to a `Date` (a
 * time zone shift would move the day) — `choice` simple → `{ value, label }`, `choice` multiple →
 * `{ value, label }[]`, `image` → always `null`.
 */
export type DocumentFieldValuePayload =
	| string
	| number
	| boolean
	| LabeledValuePayload
	| readonly LabeledValuePayload[]
	| null;

/** A citation from an interview transcript that founds a field's value. */
export type TurnEvidencePayload = {
	readonly type: 'turn';
	readonly interview: { readonly id: number };
	readonly turnPosition: number;
	readonly quote: string;
	readonly speakerLabel: string | null;
	readonly speakerKind: LabeledValuePayload | null;
	readonly startSecond: number | null;
	readonly endSecond: number | null;
};

/** A record fact from the beneficiary's file that founds a field's value. */
export type FactEvidencePayload = {
	readonly type: 'fact';
	readonly key: string;
	readonly label: string;
	readonly value: string;
};

/** A field of a source document that founds a field's value. `label` and `value` are frozen as sent
 * to the model; `itemPath` locates the field inside list items of the source document. */
export type DocumentFieldEvidencePayload = {
	readonly type: 'document_field';
	readonly document: { readonly id: number };
	readonly key: string;
	readonly fieldUuid: string;
	readonly itemPath: readonly number[];
	readonly label: string;
	readonly value: string;
};

/** A field founded on a document imported into the dossier; `name` is frozen at generation time. */
export type ImportedDocumentEvidencePayload = {
	readonly type: 'imported_document';
	readonly importedDocument: { readonly id: number };
	readonly name: string;
};

/** Discriminated on `type` — the wire calls this discriminant `type`, the storage layer calls it
 * `kind`; the front only ever reads the wire name. */
export type EvidencePayload =
	| TurnEvidencePayload
	| FactEvidencePayload
	| DocumentFieldEvidencePayload
	| ImportedDocumentEvidencePayload;

export type DocumentFieldEntryPayload = {
	readonly type: 'field';
	readonly key: string;
	readonly uuid: string;
	readonly path: string;
	readonly label: string;
	readonly description: string | null;
	readonly fieldType: LabeledValuePayload;
	readonly isMultiple: boolean;
	readonly value: DocumentFieldValuePayload;
	readonly options: readonly LabeledValuePayload[] | null;
	readonly verification: LabeledValuePayload;
	readonly isPendingReview: boolean;
	readonly isRejectedValue: boolean;
	readonly canConfirm: boolean;
	readonly reviewedBy: { readonly id: number } | null;
	readonly reviewedAt: string | null;
	readonly evidence: readonly EvidencePayload[];
};

export type DocumentGroupEntryPayload = {
	readonly type: 'group';
	readonly uuid: string;
	readonly path: string;
	readonly title: string;
	readonly description: string | null;
	readonly entries: readonly DocumentEntryPayload[];
};

export type DocumentListItemPayload = {
	readonly entries: readonly DocumentEntryPayload[];
};

export type DocumentListEntryPayload = {
	readonly type: 'list';
	readonly uuid: string;
	readonly path: string;
	readonly title: string;
	readonly description: string | null;
	/** Opaque list key, sent back as is (URL-encoded) by the item routes — never recomposed. */
	readonly key: string;
	/** `null` = unbounded. */
	readonly minItems: number | null;
	readonly maxItems: number | null;
	readonly items: readonly DocumentListItemPayload[];
};

/** A discriminated union on the literal `type` — never re-decided from anything else. */
export type DocumentEntryPayload =
	| DocumentFieldEntryPayload
	| DocumentGroupEntryPayload
	| DocumentListEntryPayload;

export type StatusCountPayload = {
	readonly status: LabeledValuePayload;
	readonly count: number;
};

/** Same shape at every scope — a section, a document — the counts are simply bounded to a
 * narrower set of fields each time. `byStatus` always carries the five verification states, in a
 * fixed order, zero counts included. */
export type VerificationProgressPayload = {
	readonly total: number;
	readonly verified: number;
	readonly remaining: number;
	/** Applicable fields whose stored value the type check rejected. */
	readonly invalid: number;
	readonly byStatus: readonly StatusCountPayload[];
};

/** `key: ''` marks the section of fields outside any node ("Général"), server-placed first
 * — never used as a display index, only as React `key`. */
export type DocumentSectionPayload = {
	readonly key: string;
	readonly title: string;
	readonly description: string | null;
	readonly progress: VerificationProgressPayload;
	readonly isNotApplicable: boolean;
	readonly entries: readonly DocumentEntryPayload[];
};

/**
 * The wire shape of `GET …/documents/{document}`, and of every write route that returns the
 * document entire (`PUT …/value`, `POST …/verifications`, `POST …/validations`, and the list item routes). Extends the
 * summary (`DocumentPayload`) with the detail-only fields, rather than redeclaring the
 * fields both share.
 */
export type DocumentDetailPayload = DocumentPayload & {
	readonly templateVersion: { readonly id: number };
	/** Exactly one of `interview` / `document` / `importedDocument` is non-null per source. */
	readonly sources: ReadonlyArray<{
		readonly interview: { readonly id: number } | null;
		readonly document: { readonly id: number } | null;
		readonly importedDocument: { readonly id: number } | null;
	}>;
	readonly validatedAt: string | null;
	readonly validatedBy: { readonly id: number } | null;
	/** `false` once the document is verified. */
	readonly canValidate: boolean;
	/** The server's verdict on deletion; `deletionBlockedReason` is its French reason, `null` iff `canDelete`. */
	readonly canDelete: boolean;
	readonly deletionBlockedReason: string | null;
	readonly sections: readonly DocumentSectionPayload[];
};

export type DocumentFetchedAction = {
	type: 'DOCUMENT_FETCHED';
	payload: DocumentDetailPayload;
	facilityId: number;
	subjectId: number;
};

export default (
	payload: DocumentDetailPayload,
	facilityId: number,
	subjectId: number,
): DocumentFetchedAction => ({
	type: 'DOCUMENT_FETCHED',
	payload,
	facilityId,
	subjectId,
});
